/** Share one in-flight Promise per key; optionally reuse the resolved value for ttlMs. */

interface CacheEntry {
  value: unknown;
  expiresAt: number;
}

const inflight = new Map<string, Promise<unknown>>();
const cache = new Map<string, CacheEntry>();

export function dedupeAsync<T>(
  key: string,
  run: () => Promise<T>,
  options?: { ttlMs?: number },
): Promise<T> {
  const ttlMs = options?.ttlMs;
  if (ttlMs != null && ttlMs > 0) {
    const cached = cache.get(key);
    if (cached && cached.expiresAt > Date.now()) {
      return Promise.resolve(cached.value as T);
    }
  }

  const existing = inflight.get(key);
  if (existing) {
    return existing as Promise<T>;
  }

  const promise = run()
    .then((value) => {
      if (ttlMs != null && ttlMs > 0) {
        cache.set(key, { value, expiresAt: Date.now() + ttlMs });
      }
      return value;
    })
    .finally(() => {
      if (inflight.get(key) === promise) {
        inflight.delete(key);
      }
    });

  inflight.set(key, promise);
  return promise;
}

export function clearDedupeCache(key?: string): void {
  if (key) {
    cache.delete(key);
    inflight.delete(key);
    return;
  }
  cache.clear();
  inflight.clear();
}

/** Drop every cache/inflight entry whose key starts with `prefix`. */
export function clearDedupeCacheByPrefix(prefix: string): void {
  for (const key of Array.from(cache.keys())) {
    if (key.startsWith(prefix)) cache.delete(key);
  }
  for (const key of Array.from(inflight.keys())) {
    if (key.startsWith(prefix)) inflight.delete(key);
  }
}
